import React, { FC, useState, useEffect, ChangeEvent } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField,
} from '@mui/material';
import { MaterialListType, MaterialPriceList } from 'models/material';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { modifyMaterialPriceListTemplate } from 'apollo/templates/material';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';

type EditMaterialPriceListDialogProps = {
    materialPriceList: MaterialPriceList;
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onEditMaterialPriceList: (priceList: MaterialPriceList) => void;
    materialPriceLists: MaterialPriceList[];
};
const EditMaterialPriceListDialog: FC<EditMaterialPriceListDialogProps> = ({
    materialPriceList,
    dialogOpen,
    onCloseDialog,
    onEditMaterialPriceList,
    materialPriceLists,
}: EditMaterialPriceListDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings']);
    const [priceListName, setPriceListName] = useState('');
    const [priceListNameError, setPriceListNameError] = useState(false);
    const [conversionRate, setConversionRate] = useState(0);
    const [isConversionRateValid, setIsConversionRateValid] = useState(true);
    const [materialListType, setMaterialListType] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();

    const [modifyMaterialPriceList] = useMutation(modifyMaterialPriceListTemplate, {
        variables: {
            _id: materialPriceList._id,
            materialPriceListName: priceListName,
            conversionRate,
        },
        onCompleted: ({ modifyMaterialPriceList }) => {
            onEditMaterialPriceList(modifyMaterialPriceList);
            onCloseDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handlePriceListNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPriceListNameError(false);
        if (!event.target.value || priceListNameAlreadyExist(event.target.value)) {
            setPriceListNameError(true);
        }
        setPriceListName(event.target.value);
    };

    const priceListNameAlreadyExist = (name: string) => {
        const priceListsWithoutCurrentPriceList = materialPriceLists.filter(
            (priceList) =>
                priceList.materialPriceListName.toLocaleLowerCase().trim() !==
                materialPriceList.materialPriceListName.toLocaleLowerCase().trim(),
        );
        const priceListNames = priceListsWithoutCurrentPriceList.map((priceList) =>
            priceList.materialPriceListName.replace(/\s/g, '').toLocaleLowerCase().trim(),
        );
        if (priceListNames.includes(name.replace(/\s/g, '').toLocaleLowerCase().trim())) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (materialPriceList) {
            setPriceListName(materialPriceList.materialPriceListName);
            if (materialPriceList.materialListType) {
                setMaterialListType(materialPriceList.materialListType);
                if (materialPriceList.materialListType === MaterialListType.CURRENCY_CONVERTED_PRICE_LIST) {
                    setConversionRate(materialPriceList.conversionRate || 0);
                }
            }
        }
    }, [materialPriceList]);

    const onConversionRateChange = (conversionRate: string) => {
        setIsConversionRateValid(true);
        const rate = conversionRate.replace(',', '.');
        if (rate) {
            if (rate === '0') {
                setIsConversionRateValid(false);
            }
            setConversionRate(parseFloat(rate));
        } else {
            setIsConversionRateValid(false);
        }
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={dialogOpen}>
            <DialogTitle>{translate('lab-settings:EDIT_PRICE_LIST')}</DialogTitle>
            <DialogContent>
                <Box mt={1} mb={2}>
                    <Grid container spacing={2}>
                        <Grid item md={8} sm={12}>
                            <FormControl fullWidth margin="dense">
                                <TextField
                                    id="material-price-list-name"
                                    label={translate('lab-settings:PRICE_LIST_NAME')}
                                    required
                                    defaultValue={priceListName}
                                    onChange={handlePriceListNameChange}
                                    error={priceListNameError}
                                    helperText={
                                        priceListNameError && !priceListName
                                            ? translate('lab-settings:VALIDATION:NAME')
                                            : priceListNameError &&
                                              translate('lab-settings:VALIDATION:PRICE_LIST_ALREADY_EXIST')
                                    }
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
                {materialListType ? (
                    <Box mt={1} mb={2}>
                        <Grid container spacing={2}>
                            <Grid item md={6} sm={12}>
                                <FormControl fullWidth component="fieldset">
                                    <RadioGroup
                                        row
                                        name="list-type"
                                        aria-disabled
                                        defaultValue={materialListType}
                                        aria-readonly
                                        value={materialPriceList.materialListType}
                                    >
                                        <FormControlLabel
                                            value="NORMAL_PRICE_LIST"
                                            disabled={true}
                                            control={
                                                <Radio
                                                    checked={materialListType === MaterialListType.NORMAL_PRICE_LIST}
                                                    color="secondary"
                                                />
                                            }
                                            label={translate('lab-settings:NORMAL_PRICE_LIST')}
                                        />
                                        <FormControlLabel
                                            value="CURRENCY_CONVERTED_PRICE_LIST"
                                            disabled={true}
                                            control={
                                                <Radio
                                                    checked={
                                                        materialListType ===
                                                        MaterialListType.CURRENCY_CONVERTED_PRICE_LIST
                                                    }
                                                    color="secondary"
                                                />
                                            }
                                            label={translate('lab-settings:CURRENCY_CONVERTED_PRICE_LIST')}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ) : undefined}
                {materialListType && materialListType === MaterialListType.CURRENCY_CONVERTED_PRICE_LIST ? (
                    <Box mt={1} mb={2}>
                        <Grid container spacing={2}>
                            <Grid item md={6} sm={12}>
                                <FormControl fullWidth>
                                    <NumberOnlyTextField
                                        id="conversion-rate"
                                        size="small"
                                        label={translate('lab-settings:CURRENCY_CONVERSION_RATE')}
                                        onValueChange={onConversionRateChange}
                                        value={conversionRate}
                                        required
                                        error={!isConversionRateValid}
                                        helperText={
                                            !isConversionRateValid &&
                                            translate('lab-settings:VALIDATION:CURRENCY_CONVERSION_RATE')
                                        }
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ) : undefined}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseDialog}>{translate('common:CANCEL')}</Button>
                <Button
                    variant="contained"
                    disabled={priceListNameError || !isConversionRateValid}
                    onClick={() => modifyMaterialPriceList()}
                >
                    {translate('lab-settings:SAVE_PRICE_LIST')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditMaterialPriceListDialog;
